import { useEffect, useMemo, useState } from "react";
import ParagraphEditor from "./ParagraphEditor";
import {
    CONTENT_DIFFICULTY_OPTIONS,
    CONTENT_LEVEL_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    emptyContentDraft,
    validateContentDraft,
} from "../utils/contentUtils";

const ContentFormModal = ({
    open,
    title,
    initialDraft,
    error,
    isSubmitting,
    onClose,
    onSubmit,
}) => {
    const [draft, setDraft] = useState(emptyContentDraft);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        setDraft(initialDraft || emptyContentDraft);
        setFieldErrors({});
    }, [open, initialDraft]);

    const isEditMode = useMemo(() => title?.toLowerCase().includes("edit"), [title]);

    if (!open) return null;

    const updateDraft = (name, value) => {
        setDraft((current) => ({ ...current, [name]: value }));
    };

    const handleParagraphChange = (id, value) => {
        setDraft((current) => ({
            ...current,
            paragraphs: current.paragraphs.map((item) =>
                item.id === id ? { ...item, paragraph: value } : item
            ),
        }));
    };

    const handleAddParagraph = () => {
        setDraft((current) => ({
            ...current,
            paragraphs: [...current.paragraphs, { id: current.paragraphs.length + 1, paragraph: "" }],
        }));
    };

    const handleRemoveParagraph = (id) => {
        setDraft((current) => {
            const updated = current.paragraphs.filter((item) => item.id !== id);
            return {
                ...current,
                paragraphs: updated.map((item, index) => ({ ...item, id: index + 1 })),
            };
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const validation = validateContentDraft(draft);
        if (!validation.isValid) {
            setFieldErrors(validation.errors);
            return;
        }

        setFieldErrors({});
        onSubmit(draft);
    };

    return (
        <div className="content-modal-overlay" role="presentation" onClick={onClose}>
            <section
                className="content-modal"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(event) => event.stopPropagation()}
            >
                <header>
                    <h2>{title}</h2>
                    <button type="button" className="content-link-btn" onClick={onClose}>
                        Close
                    </button>
                </header>

                {error && (
                    <p className="content-form-error" role="alert">
                        {error}
                    </p>
                )}

                <form onSubmit={handleSubmit} className="content-form">
                    <label className="lms-field">
                        <span>Title</span>
                        <input
                            name="title"
                            value={draft.title}
                            onChange={(event) => updateDraft("title", event.target.value)}
                            placeholder="The Clever Fox"
                        />
                        {fieldErrors.title && <small>{fieldErrors.title}</small>}
                    </label>

                    <div className="content-form-grid">
                        <label className="lms-field">
                            <span>Author</span>
                            <input
                                name="author"
                                value={draft.author}
                                onChange={(event) => updateDraft("author", event.target.value)}
                            />
                        </label>
                        <label className="lms-field">
                            <span>Level</span>
                            <select
                                name="level"
                                value={draft.level}
                                onChange={(event) => updateDraft("level", event.target.value)}
                            >
                                {CONTENT_LEVEL_OPTIONS.map((level) => (
                                    <option key={level} value={level}>
                                        {level}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="content-form-grid">
                        <label className="lms-field">
                            <span>Difficulty</span>
                            <select
                                name="difficulty"
                                value={draft.difficulty}
                                onChange={(event) => updateDraft("difficulty", event.target.value)}
                            >
                                {CONTENT_DIFFICULTY_OPTIONS.map((difficulty) => (
                                    <option key={difficulty} value={difficulty}>
                                        {difficulty}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="lms-field">
                            <span>Content type</span>
                            <select
                                name="contentType"
                                value={draft.contentType}
                                onChange={(event) => updateDraft("contentType", event.target.value)}
                            >
                                {CONTENT_TYPE_OPTIONS.map((item) => (
                                    <option key={item} value={item}>
                                        {item}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <label className="lms-field">
                        <span>Cover image URL</span>
                        <input
                            name="imageUrl"
                            value={draft.imageUrl}
                            onChange={(event) => updateDraft("imageUrl", event.target.value)}
                            placeholder="https://example.com/cover.jpg"
                        />
                    </label>

                    <label className="lms-field">
                        <span>Categories (comma separated)</span>
                        <input
                            name="categories"
                            value={draft.categoriesInput}
                            onChange={(event) => updateDraft("categoriesInput", event.target.value)}
                            placeholder="Fiction, Animals"
                        />
                    </label>

                    <ParagraphEditor
                        paragraphs={draft.paragraphs}
                        onChange={handleParagraphChange}
                        onAdd={handleAddParagraph}
                        onRemove={handleRemoveParagraph}
                    />
                    {fieldErrors.paragraphs && <p className="content-form-error">{fieldErrors.paragraphs}</p>}

                    <div className="content-form-actions">
                        <button type="button" className="student-secondary-action" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="lms-primary-action" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "Saving..." : isEditMode ? "Save changes" : "Create content"}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default ContentFormModal;
